import { DestroyRef, Injectable, inject, reflectComponentType } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { FIREBASE_APP } from '../core/firebase.providers';
import { FIREBASE_AUTH } from './firebase.auth';
import { Analytics, getAnalytics, isSupported, logEvent, setUserId } from 'firebase/analytics';
import { onAuthStateChanged } from 'firebase/auth';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FirebaseAnalyticsService {
  private readonly app = inject(FIREBASE_APP);
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analytics = this.initialize();
  private userId: string | null | undefined;
  private trackingStarted = false;
  private previousScreen: Record<string, string | number> | undefined;
  private readonly screenIds = new Map<string, number>();
  private nextScreenId = Math.floor(Math.random() * (2 ** 32)) - 2 ** 31;

  private async initialize(): Promise<Analytics | null> {
    try {
      if (!await isSupported() || this.destroyRef.destroyed) return null;
      const analytics = getAnalytics(this.app);
      // Restore persisted authentication before attributing any queued events.
      await this.auth.authStateReady();
      if (this.destroyRef.destroyed) return null;
      this.updateUserId(analytics, this.auth.currentUser?.uid ?? null);
      const unsubscribe = onAuthStateChanged(this.auth, user => {
        try {
          this.updateUserId(analytics, user?.uid ?? null);
        } catch (error) {
          console.error('Error updating analytics user identity:', error);
        }
      });
      this.destroyRef.onDestroy(unsubscribe);
      return analytics;
    } catch (error) {
      console.error('Error initializing analytics:', error);
      return null;
    }
  }

  private updateUserId(analytics: Analytics, userId: string | null): void {
    if (this.userId === userId) return;
    setUserId(analytics, userId);
    this.userId = userId;
  }

  logEvent(name: string, params: Record<string, unknown>): void {
    void this.analytics.then(analytics => {
      if (!analytics || this.destroyRef.destroyed) return;
      // Also synchronize here in case an action precedes the auth-state callback.
      this.updateUserId(analytics, this.auth.currentUser?.uid ?? null);
      logEvent(analytics, name, params);
    }).catch(error => console.error(`Error logging event '${name}':`, error));
  }

  startTracking(): void {
    if (this.trackingStarted) return;
    this.trackingStarted = true;
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => this.trackScreen(event.urlAfterRedirects));
  }

  private trackScreen(url: string): void {
    let route = this.router.routerState.snapshot.root;
    while (true) {
      const child = route.children.find(child => child.outlet === PRIMARY_OUTLET);
      if (!child) break;
      route = child;
    }
    const component = route.component;
    if (!component) return;
    const screenClass = typeof component === 'string'
      ? component
      : reflectComponentType(component)?.selector;
    if (!screenClass) return;
    const screenName = route.pathFromRoot.map(part => part.routeConfig?.path).filter(Boolean).join('/') || '/';
    // Omit query strings/fragments, including email-link authentication tokens.
    const pagePath = '/' + (this.router.parseUrl(url).root.children[PRIMARY_OUTLET]?.toString() ?? '');
    const screen = {
      screen_name: screenName, screen_class: screenClass,
      page_path: pagePath, page_title: this.title.getTitle(),
    };
    if (this.previousScreen && Object.entries(screen).every(([key, value]) => this.previousScreen![key] === value)) return;
    const screenKey = `${screenClass}#${PRIMARY_OUTLET}`;
    if (!this.screenIds.has(screenKey)) this.screenIds.set(screenKey, this.nextScreenId++);
    const params = {
      ...screen,
      firebase_screen: screenName, firebase_screen_class: screenClass,
      firebase_screen_id: this.screenIds.get(screenKey)!,
      firebase_event_origin: 'auto', outlet: PRIMARY_OUTLET,
      ...(this.previousScreen ? {
        firebase_previous_screen: this.previousScreen['screen_name'],
        firebase_previous_class: this.previousScreen['screen_class'],
        firebase_previous_id: this.previousScreen['firebase_screen_id'],
      } : {}),
    };
    this.previousScreen = params;
    this.logEvent('screen_view', params);
  }
}
